import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import AppShell from "../components/ui/AppShell";
import { Card, EmptyState, ProgressRing, SectionHeading, TextLink } from "../components/ui";
import { Stagger, StaggerItem } from "../components/ui/motion";
import { Award, Clock, Path } from "../components/icons";
import LearningPathCard from "../components/learning/LearningPathCard";
import AssignedItemCard from "../components/learning/AssignedItemCard";
import VoiceTrainingCard from "../components/learning/VoiceTrainingCard";

import { getMyAssignments } from "../services/AssignmentService";
import { getMyVoiceTraining } from "../services/VoiceTrainingService";
import { openAssignedItem, summarisePlan } from "../lib/learning";

/**
 * "My Learning Plan" — everything a trainer has assigned to this learner.
 *
 * Two groups, because they behave differently and shouldn't be mixed into one
 * wall of cards: learning paths run in order and are shown as journeys;
 * standalone items can be taken anytime and are shown as a grid. Three figures
 * sit above both so "am I on track" is answered before any scrolling.
 *
 * All status, ordering and availability come from `/api/assignment/me` — this
 * page decides nothing about completion.
 */
export const MyLearning = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [voice, setVoice] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [assigned, voiceItems] = await Promise.all([
        getMyAssignments(),
        getMyVoiceTraining(),
      ]);
      setItems(assigned);
      setVoice(voiceItems);
      setLoading(false);
    })();
  }, []);

  // `openAssignedItem` refuses rather than guessing when it can't place an item
  // (e.g. its module was unpublished after being assigned). Tell the learner —
  // silently doing nothing, or opening something else, is worse than a message.
  const open = (item) => {
    if (!openAssignedItem(item, navigate)) {
      toast.error(
        "This item can't be opened right now. Please contact your training team."
      );
    }
  };

  const summary = useMemo(() => summarisePlan(items), [items]);

  const paths = useMemo(
    () => items.filter((i) => i.contentType === "learning-path"),
    [items]
  );
  const singles = useMemo(
    () => items.filter((i) => i.contentType !== "learning-path"),
    [items]
  );

  /**
   * Certificates the learner has actually earned — completed modules and paths,
   * the only two types the certificate endpoint will issue for.
   */
  const certificates = useMemo(
    () =>
      items.filter(
        (i) =>
          i.status === "completed" &&
          (i.contentType === "ppt-module" ||
            (i.contentType === "learning-path" &&
              (i.certificate?.issued || i.certificate?.eligible !== false)))
      ).length,
    [items]
  );

  return (
    <AppShell crumb="My Learning Plan" active="plan">
      <div className="ds-section__head" style={{ marginTop: 0 }}>
        <div>
          <h1 className="ds-h1">My Learning Plan</h1>
          <p className="ds-sub">
            Everything your trainer assigned — learning paths run in order,
            standalone items can be taken anytime.
          </p>
        </div>
        <TextLink onClick={() => navigate("/scorm-courses")}>
          SCORM courses
        </TextLink>
      </div>

      {loading ? (
        <p className="ds-sub">Loading your plan…</p>
      ) : items.length === 0 && voice.length === 0 ? (
        <Card>
          <EmptyState
            icon={Path}
            text="Nothing has been assigned to you yet. Your trainer will add items to this plan — check back soon."
          />
        </Card>
      ) : (
        <>
          {items.length > 0 && (
            <div className="ds-grid ds-grid--3">
              <Card>
                <div className="ds-stat">
                  <ProgressRing
                    value={summary.percentComplete}
                    size={46}
                    tone={summary.percentComplete === 100 ? "success" : "brand"}
                  />
                  <div>
                    <div className="ds-stat__value">
                      {summary.completed} / {summary.total}
                    </div>
                    <div className="ds-stat__label">Items completed</div>
                  </div>
                </div>
              </Card>

              <Card>
                <div className="ds-stat">
                  <span className="ds-tile ds-tile--lg" style={{ background: "var(--warn-bg)", color: "var(--warn-ink)" }}>
                    <Clock size={20} />
                  </span>
                  <div>
                    <div className="ds-stat__value">{summary["in-progress"]}</div>
                    <div className="ds-stat__label">In progress</div>
                  </div>
                </div>
              </Card>

              <Card>
                <div className="ds-stat">
                  <span className="ds-tile ds-tile--lg" style={{ background: "var(--award-bg)", color: "var(--award)" }}>
                    <Award size={20} />
                  </span>
                  <div>
                    <div className="ds-stat__value">{certificates}</div>
                    <div className="ds-stat__label">Certificates earned</div>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {voice.length > 0 && (
            <section className="ds-section">
              <SectionHeading
                title="Voice practice"
                description="Role-play a sales call with the AI coach. Your trainer sees the results."
              />
              <Stagger className="ds-grid ds-grid--3">
                {voice.map((item) => (
                  <StaggerItem key={item.assignmentId}>
                    <VoiceTrainingCard item={item} />
                  </StaggerItem>
                ))}
              </Stagger>
            </section>
          )}

          {paths.length > 0 && (
            <section className="ds-section">
              <SectionHeading
                title="Learning paths"
                description="Steps unlock in order — finish one to move to the next."
              />
              <div className="ds-stack ds-gap-4">
                {paths.map((item) => (
                  <LearningPathCard key={item._id} item={item} onOpenStep={open} />
                ))}
              </div>
            </section>
          )}

          {singles.length > 0 && (
            <section className="ds-section">
              <SectionHeading
                title="Standalone items"
                description="Individual modules, lessons and courses assigned to you."
              />
              <Stagger className="ds-grid ds-grid--3">
                {singles.map((item) => (
                  <StaggerItem key={item._id}>
                    <AssignedItemCard item={item} onOpen={open} />
                  </StaggerItem>
                ))}
              </Stagger>
            </section>
          )}
        </>
      )}
    </AppShell>
  );
};

export default MyLearning;
